<template>
	<section class="w-full">
		<!-- Meta tags -->
		<Title>{{ $t("seo.pages.blog.title") }}</Title>
		<Meta name="description" :content="$t('seo.pages.blog.description')" />
		<Link v-if="globalConfig.isBlue" rel="canonical" :href="`https://dreamjobs.hu${$route.fullPath}`" />
		<Link v-else rel="canonical" :href="`https://${globalConfig.host}${$route.fullPath}`" />
		<Link v-if="globalConfig.isBlue" rel="icon" type="image/x-icon" href="/blue-favicon.ico?v3" />
		<Link v-else rel="icon" type="image/x-icon" href="/favicon.ico?v3" />
		<Meta property="og:url" :content="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:type" content="website" />
		<Meta property="og:title" :content="$t('seo.pages.blog.title')" />
		<Meta property="og:description" :content="$t('seo.pages.blog.description')" />
		<Meta
			property="og:locale"
			:content="`${$i18n.locale}_${$i18n.locale == 'en' ? 'US' : $i18n.locale.toUpperCase()}`"
		/>
		<Meta
			property="og:image"
			content="https://res.cloudinary.com/djbs/image/upload/v1669288084/content/DreamJobs/Graphics/ogimage_dreamjobs.png"
		/>
		<Meta property="og:image:type" content="image/jpeg" />
		<Meta property="og:image:width" content="1200" />
		<Meta property="og:image:height" content="628" />

		<BannerContainer banner-type="frontend-companies-banner" class="banner-section" v-slot="{ content }">
			<section
				class="container relative flex flex-col md:flex-row justify-center mx-5 sm:mx-8 md:mx-auto my-8"
				v-html="content"
			></section>
		</BannerContainer>
		<AdaptiveContainer>
			<div class="flex flex-col col-span-4">
				<h1 class="text-5xl font-semibold mb-dj-30 text-center md:text-left">Blog</h1>
				<div class="flex justify-center flex-wrap gap-[5px] mb-dj-30 md:mb-dj-60">
					<BaseTagPill
						@click="clearCategory"
						class="cursor-pointer"
						:type="selectedCategory == null ? 'pink' : 'cyan'"
					>
						{{ $t("blogPage.all") }}
					</BaseTagPill>
					<BaseTagPill
						@click="setCategory(category.id)"
						class="cursor-pointer"
						v-for="(category, index) in blogCategories"
						:key="index"
						:type="selectedCategory == category.id ? 'pink' : 'cyan'"
					>
						{{ category.name[locale] }}
					</BaseTagPill>
				</div>
			</div>
		</AdaptiveContainer>
		<AdaptiveContainer id="blogListScrollTarget">
			<TransitionGroup name="list">
				<CardBlog v-for="post in blogData" :key="post.id" :post="post" newTab />
			</TransitionGroup>
		</AdaptiveContainer>
		<div class="flex justify-center w-full">
			<BasePagination
				v-if="blogData.length != 0"
				v-model="paginationModel"
				:total="paginationTotalPages"
				class="my-6"
				@change-page="setPage"
				:next-lang="$t('paginationNext')"
				:prev-lang="$t('paginationPrev')"
			/>
		</div>
		<div class="w-full bg-gradient-to-b from-[#F4F5FB] to-white">
			<template v-if="isAuth">
				<JobsPageCarousel
					:title="$t('jobs.recommended')"
					:carousel-data-url="recommendedCarouselUrl"
					:data-url="recommendedDataUrl"
					route-param="allRecommendedJobs"
					@data-fetched="processApiData"
					carouselType="offered-jobs"
				/>
			</template>
			<template v-else>
				<SectionsJobs />
			</template>
			<JobsPageCarousel
				:title="$t('jobs.withSalary')"
				:carousel-data-url="`jobs?scope[]=hasVisibleSalary${
					globalConfig.isBlue ? '&scope[]=isBlue' : globalConfig.isNotBlueHU ? '&scope[]=isNotBlue' : ''
				}`"
				:data-url="salaryDataUrl"
				@data-fetched="processApiData"
				route-param="jobsWithSalary"
				carouselType="jobs-with-salary"
			/>
		</div>
	</section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { constructApiUrl } from "@@/composables/helpers";

type BlogPost = {
	id: number | string;
	[key: string]: unknown;
};

type BlogCategory = {
	id: number;
	name: Record<string, string>;
};

type BlogCategoriesResponse = {
	data?: BlogCategory[];
};

type BlogListResponse = {
	data?: BlogPost[];
	pagination?: {
		last_page?: number;
	};
};

const route = useRoute();
const router = useRouter();
const { locale } = useI18n();

const user = useAuth.getUser();
const currentRegion = useRegion.getCurrentRegion().code.toLowerCase();

const isAuth = useState("isAuth");

const selectedCategory = computed<number | null>(() => {
	const routeCategory = Array.isArray(route.query.category) ? route.query.category[0] : route.query.category;
	return routeCategory ? parseInt(routeCategory) : null;
});

const recommendedCarouselUrl = ref("jobs?");
const recommendedDataUrl = ref(globalConfig.API);
const salaryDataUrl = ref(globalConfig.API + "/jobs?scope[]=hasVisibleSalary");

const paginationModel = ref(
	route.query && route.query.page && parseInt(String(route.query.page)) > 0 ? parseInt(String(route.query.page)) : 1
);

const blogPostsUrl = computed(() => {
	const dataUrl = constructApiUrl("/posts");
	dataUrl.searchParams.append("region", currentRegion);
	dataUrl.searchParams.append("per_page", "16");
	dataUrl.searchParams.append("page", route.query.page ? String(route.query.page) : "1");
	if (route.query.category) {
		dataUrl.searchParams.append("category", String(route.query.category));
	}
	return dataUrl.href;
});

const { data: blogCategoriesResponse } = await useFetch<BlogCategoriesResponse>(`${globalConfig.API}/post-categories`, {
	server: true,
	key: `blog-categories-${currentRegion}`,
	default: () => ({ data: [] }),
});

const { data: blogPostsResponseRaw, refresh: refreshBlog } = await useFetch<BlogListResponse>(
	() => blogPostsUrl.value,
	{
		server: true,
		key: "blog-posts",
		default: () => ({ data: [], pagination: { last_page: 1 } }),
	}
);

const blogPostsResponse = computed<BlogListResponse>(
	() =>
		(blogPostsResponseRaw.value as BlogListResponse | undefined) || {
			data: [],
			pagination: { last_page: 1 },
		}
);

const blogData = computed<BlogPost[]>(() => blogPostsResponse.value?.data || []);
const blogCategories = computed<BlogCategory[]>(() => blogCategoriesResponse.value?.data || []);
const paginationTotalPages = computed<number>(() => blogPostsResponse.value?.pagination?.last_page || 1);

watch(
	() => route.query,
	async () => {
		const routePage = Array.isArray(route.query.page) ? route.query.page[0] : route.query.page;
		if (routePage && parseInt(routePage) > 0) {
			paginationModel.value = parseInt(routePage);
		} else {
			paginationModel.value = 1;
		}

		await refreshBlog();

		if (process.client) {
			const target = document.getElementById("blogListScrollTarget");
			if (target) {
				window.scrollTo({ top: target.offsetTop - 76, behavior: "smooth" });
			}
		}
	}
);

function buildRecommendedUrl() {
	if (!user?.tags) {
		return;
	}

	// region
	recommendedCarouselUrl.value += `region=${useRegion.getCurrentRegion().code.toLowerCase()}`;
	// categories
	if (user.tags.job_category) {
		user.tags.job_category.data.forEach((cat) => {
			recommendedCarouselUrl.value += `&tags[job-category][]=${cat.id}`;
		});
	}
	// subcategories
	if (user.tags.job_subcategory) {
		user.tags.job_subcategory.data.forEach((cat, index) => {
			if (index == 0 && recommendedCarouselUrl.value.match(new RegExp("tags", "gi")) == null) {
				recommendedCarouselUrl.value += `tags[job-subcategory][]=${cat.id}`;
			} else {
				recommendedCarouselUrl.value += `&tags[job-subcategory][]=${cat.id}`;
			}
		});
	}
	// job levels
	if (user.tags.job_level) {
		user.tags.job_level.data.forEach((level, index) => {
			if (index == 0 && recommendedCarouselUrl.value.match(new RegExp("tags", "gi")) == null) {
				recommendedCarouselUrl.value += `tags[job-level][]=${level.id}`;
			} else {
				recommendedCarouselUrl.value += `&tags[job-level][]=${level.id}`;
			}
		});
	}

	if (globalConfig.isBlue) {
		recommendedCarouselUrl.value += "&scope[]=isBlue";
	}
	if (globalConfig.isNotBlueHU) {
		recommendedCarouselUrl.value += "&scope[]=isNotBlue";
	}

	recommendedDataUrl.value += `/${recommendedCarouselUrl.value}`;
}

function processApiData(event: { param: string }) {
	const router = useRouter();
	const query: Record<string, string> = {};
	query[event.param] = "true";
	router.push({
		path: `/${locale.value}/jobs`,
		query: query,
	});
}

if (isAuth.value) {
	buildRecommendedUrl();
}

salaryDataUrl.value += `&region=${currentRegion}`;
if (globalConfig.isBlue) {
	salaryDataUrl.value += "&scope[]=isBlue";
}
if (globalConfig.isNotBlueHU) {
	salaryDataUrl.value += "&scope[]=isNotBlue";
}

function setPage(page: number) {
	router.push({
		path: `/${locale.value}/blog`,
		query: {
			...route.query,
			page: page > 1 ? page : undefined,
		},
	});
}

async function setCategory(id: number, firstPage = true) {
	router.push({
		path: `/${locale.value}/blog`,
		query: {
			...route.query,
			...(firstPage == true && { page: undefined }),
			category: id,
		},
	});
}

function clearCategory() {
	router.push(`/${locale.value}/blog`);
}
</script>

<script lang="ts">
export default {
	name: "BlogPage",
};
</script>

<style>
.list-enter-active,
.list-leave-active {
	transition: all 0.5s ease;
}
.list-enter-from,
.list-leave-to {
	opacity: 0;
	transform: translateX(30px);
}
</style>
